// ============================================================================
// The browser half — a queue and a beacon, and nothing that knows who you are
// ============================================================================
//
// This exists for the events a server seam cannot see: which tab somebody
// opened, which filter they used, that they exported a report. It does NOT
// carry identity — the ingest route stamps that from the session cookie — and
// it is therefore safe for it to be as dumb as it looks.
//
// Two behaviours are load-bearing:
//
//  1. IT BATCHES. One request per flush window, not one per click. The GA layer
//     learned this; a per-event fetch on a list view is a self-inflicted load
//     test.
//  2. IT USES `sendBeacon` ON UNLOAD. A page_view recorded as the user
//     navigates away is exactly the one a normal `fetch` drops, and "they
//     opened it and left" is a real answer.
//
// Storage: none. No localStorage, no cookie of its own. The session id is held
// in memory for the life of the tab, so a page that never flushes loses at most
// its own queue — which is the correct failure for telemetry.
// ============================================================================

import type { UsageCatalog } from "../catalog";

export interface UsageTrackerOptions<Name extends string> {
  readonly catalog: UsageCatalog<Name>;
  /** Where the app mounted `createUsageIngestHandler`. */
  readonly endpoint: string;
  /** Flush cadence. 5s is well inside a page visit and well outside a click burst. */
  readonly flushIntervalMs?: number;
  /** Ceiling on the queue. Beyond it the OLDEST events are dropped, not the newest. */
  readonly maxQueued?: number;
  readonly onError?: (error: unknown) => void;
}

export interface UsageTracker<Name extends string> {
  track(event: Name, detail?: UsageTrackDetail): void;
  flush(): void;
  /** Detach timers and listeners. Every consumer must call this on unmount. */
  dispose(): void;
}

export interface UsageTrackDetail {
  readonly surface?: string;
  readonly objectType?: string;
  readonly objectId?: string;
  readonly props?: Record<string, string | number | boolean | null>;
}

interface QueuedEvent extends UsageTrackDetail {
  readonly event: string;
  readonly sessionId: string;
}

function newSessionId(): string {
  const cryptoRef = typeof globalThis === "object" ? globalThis.crypto : undefined;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") return cryptoRef.randomUUID();
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createUsageTracker<Name extends string>(
  options: UsageTrackerOptions<Name>,
): UsageTracker<Name> {
  const {
    catalog,
    endpoint,
    flushIntervalMs = 5_000,
    maxQueued = 100,
    onError = () => {},
  } = options;

  const sessionId = newSessionId();
  let queue: QueuedEvent[] = [];
  let timer: ReturnType<typeof setInterval> | null = null;
  let disposed = false;

  function send(useBeacon: boolean): void {
    if (queue.length === 0) return;
    const payload = JSON.stringify({ events: queue });
    queue = [];
    try {
      if (useBeacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon(endpoint, new Blob([payload], { type: "application/json" }));
        return;
      }
      void fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: payload,
        keepalive: true,
        credentials: "same-origin",
      }).catch(onError);
    } catch (error: unknown) {
      onError(error);
    }
  }

  const onPageHide = (): void => send(true);

  if (typeof window !== "undefined") {
    timer = setInterval(() => send(false), flushIntervalMs);
    // `pagehide` fires in cases `beforeunload` does not (bfcache, mobile Safari
    // backgrounding), which is most of how a phone leaves a page.
    window.addEventListener("pagehide", onPageHide);
  }

  return {
    track(event, detail) {
      if (disposed) return;
      if (!catalog.isKnownEvent(event)) return;
      if (queue.length >= maxQueued) {
        // Drop the OLDEST. A full queue means something is looping, and the
        // recent events are the ones that describe what it is doing now.
        queue.shift();
      }
      queue.push({ ...detail, event, sessionId });
    },
    flush() {
      send(false);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      send(true);
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
      if (typeof window !== "undefined") {
        // Removing by the SAME reference. Handing `removeEventListener` a fresh
        // inline function matches nothing and removes nothing — the second of
        // the four shapes in the 2026-09 leak sweep.
        window.removeEventListener("pagehide", onPageHide);
      }
    },
  };
}
